import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Text,
} from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { useGSCtrlStore } from 'components/sections/admin-portal/server-controls/game-status/use-game-status-controls-store';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { isNil } from 'lodash';
import { useEffect } from 'react';
import { AuthService } from 'services/auth.service';

export function GameStatusControls() {
  const enabled = useGSCtrlStore((s) => s.enabled);
  const override = useGSCtrlStore((s) => s.override);

  // sync w/ server state for starting values of enabled and override
  useEffect(() => {
    AuthService.getInstance()
      .getCheckGameStatus()
      .then((value) => {
        if (typeof value === 'boolean') {
          useGSCtrlStore.getState().setEnabled(value);
        }
      });

    AuthService.getInstance()
      .readGameStatusOverride()
      .then((value) => useGSCtrlStore.getState().setOverride(value, true));
  }, []);

  return (
    <ErrorBoundary identifier="GameStatusControls">
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell width="200px" align="center">
              Actions
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell width="100px" align="center">
              Current
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            {/* actions */}
            <Table.Cell align="center">
              <Flex
                direction="column"
                gap={RADIX.FLEX.GAP.SM}
                pt={RADIX.FLEX.PAD.SM}
                pb={RADIX.FLEX.PAD.SM}
              >
                <Box>
                  <Button
                    color={enabled ? RADIX.COLOR.WARNING : RADIX.COLOR.SUCCESS}
                    size={RADIX.BUTTON.SIZE.SM}
                    className="btn-block"
                    onClick={() => useGSCtrlStore.getState().toggleEnabled()}
                  >
                    {enabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Flex>
            </Table.Cell>
            {/* current */}
            <Table.Cell align="center">
              <Badge color={enabled ? RADIX.COLOR.SUCCESS : undefined}>
                {enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </Table.Cell>
            {/* description */}
            <Table.Cell>
              <Flex
                p={RADIX.FLEX.PAD.SM}
                direction="column"
                gap={RADIX.FLEX.GAP.SM}
              >
                <Heading size={RADIX.HEADING.SIZE.SM}>
                  Game Status Checks
                </Heading>

                <Box>
                  <Text wrap="pretty">
                    This controls whether the server is contacting the Batrack
                    API to determine whether teams with MLB abbreviations are
                    currently participating in any games (i.e. whether they
                    should be placed in offline mode).
                  </Text>
                </Box>
              </Flex>
            </Table.Cell>
          </Table.Row>

          <Table.Row>
            {/* actions */}
            <Table.Cell align="center">
              <Flex
                direction="column"
                gap={RADIX.FLEX.GAP.SM}
                pt={RADIX.FLEX.PAD.SM}
                pb={RADIX.FLEX.PAD.SM}
              >
                <Box>
                  <CommonSelectInput
                    id="game-status-override"
                    name="override"
                    options={[
                      {
                        label: 'In Progress',
                        value: 'true',
                      },
                      {
                        label: 'No Game',
                        value: 'false',
                      },
                    ]}
                    optional
                    value={override?.toString() ?? undefined}
                    onOptionalBooleanChange={(v) =>
                      useGSCtrlStore
                        .getState()
                        .setOverride(typeof v === 'boolean' ? v : null, false)
                    }
                  />
                </Box>
                <Box>
                  <Button
                    size={RADIX.BUTTON.SIZE.SM}
                    title="Clear any game status override and resume regular behavior."
                    color={RADIX.COLOR.WARNING}
                    className="btn-block"
                    onClick={() =>
                      useGSCtrlStore.getState().setOverride(null, false)
                    }
                  >
                    Reset
                  </Button>
                </Box>
              </Flex>
            </Table.Cell>
            {/* current */}
            <Table.Cell align="center">
              {isNil(override) ? (
                <Badge color={RADIX.COLOR.NEUTRAL}>Not Overriding</Badge>
              ) : (
                <Badge
                  color={override ? RADIX.COLOR.WARNING : RADIX.COLOR.SUCCESS}
                >
                  {override ? 'In Progress' : 'No Game'}
                </Badge>
              )}
            </Table.Cell>
            {/* description */}
            <Table.Cell>
              <Flex
                p={RADIX.FLEX.PAD.SM}
                direction="column"
                gap={RADIX.FLEX.GAP.SM}
              >
                <Heading size={RADIX.HEADING.SIZE.SM}>
                  Game Status Override
                </Heading>

                <Box>
                  <Text wrap="pretty">
                    This controls whether the server should override any game
                    status results from the Batrack API with a static value set
                    here (e.g. for testing purposes).
                  </Text>
                </Box>
              </Flex>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>
    </ErrorBoundary>
  );
}
